import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { Grid, IconButton, Switch } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Percent } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faArrowCircleLeft, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import {
  MoneyPrefix,
  useFetchAPIs,
  useAppDispatch,
  useAppSelector,
  useCustomItemOptions,
  sendAnalyticsData,
  fetchPerfHistPerf,
  IRendimentoSubclasse,
  setSelectedBenchmarksPerfHist,
  formatDatesApi,
  formatDates,
  formatData,
  formatDataEvol,
  formatDadosMesaMesAgrupados,
  getColumnsRetornos,
  formatRowsRetornos,
  getColumnsAplRes,
  formatRowsAplRes,
  getColsTabelaRendClasseMes,
  formatRowsTabelaRendClasseMes,
  formatRowsTabelaRendClasseMesPercent,
  formatBarChartModal,
  formatBarChartRend12Data,
  formatBarChartModalPercent,
  formatWaterfallChartModal,
} from '@monorepo-web/logic-extrato';
import { helperFormat, FormatTypes, selecionarBenchmarks, useIsMobile } from '@monorepo-web/utils';
import {
  CustomModal,
  Subtitulo,
  EnhancedTable,
  Line,
  GraficoBarra,
  GraficoWaterfall,
} from '@monorepo-web/components-library';

import LoadingModal from '../LoadingModal';
import HeaderWrapper from '../../MainLayout/HeaderWrapper';
import { GraficoContribuicaoFinanceira, GraficoEvolucaoPatrimonio, GraficoGanhoFinanceiro, GraficoRetornoAcumulado, TabelaAplicacoesResgate, TabelaRetornosNominais } from '@monorepo-web/templates';

interface Props {
  dispatchPDF?: boolean;
  sendData?: any;
}

export default function PerformanceHistorica({ dispatchPDF, sendData }: Props) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const uselocation = useLocation();
  const isMobile = useIsMobile();

  const [
    obj_datas,
    dadosMesaMes,
    graficoRetornos,
    graficoPL,
    movimentacoes,
    rendimento12meses,
    loading,
    loadingPercent,
    isLoaded,
    selectedBenchmarks,
    carteiraList,
    loadedCarteiraList,
    carteira,
    isLoadingMinimized,
    params,
    dadosPercentPerf,
    preferenceCustomizacao,
  ] = useAppSelector((state) => [
    state.perfHist.obj_datas,
    state.perfHist.dadosMesaMes,
    state.perfHist.graficoRetornos,
    state.perfHist.graficoPL,
    state.perfHist.movimentacoes,
    state.perfHist.rendimento12meses,
    state.perfHist.loading,
    state.perfHist.loadingPercent,
    state.perfHist.isLoaded,
    state.perfHist.selectedBenchmarks,
    state.preference.carteiraList,
    state.preference.loadedCarteiraList,
    state.preference.params.carteira,
    state.loadMinimized.isLoadingMinimized,
    state.preference.params,
    state.perfHist.dadosPercentPerf,
    state.preference.preferenceStyle.customizacao,
  ]);

  const customItemOptions = useCustomItemOptions(loadedCarteiraList, carteiraList, carteira);

  /**
   * Selecionar benchmarks
   * @param newBenchmarks Benchmarks selecionados
   * @param historyReplace Indica se a nova navegação no state do History vai adicionar uma nova entrada ou substituir a atual no history Stack
   * No caso do benchmark inicial(preferencia do usuario), a entrada vai substituir o state atual, adicionando os benchmarks no state
   * No caso dos benchmarks selecionados, será adicionado uma nova entrada na History, para guardar a navegação desses benchmarks no history Stack
   */
  const dispatchSelectedBenchmarks = (newBenchmarks: string[], historyReplace = false) => {
    const url = `?carteira=${carteira}&periodo=${params.periodo}`;
    // o parâmetro replace permite alterar o state sem adicionar uma nova entrada na history stack
    navigate(url, {
      replace: historyReplace,
      state: { ...uselocation.state, bench_perf_hist: newBenchmarks },
    }); // adiciona os benchmarks selecionados no state do History router
    dispatch(setSelectedBenchmarksPerfHist(newBenchmarks));
  };

  useEffect(() => {
    if (!dispatchPDF)
      document.title = `${(window as any).env.REACT_APP_TABNAME} - Performance Histórica`;
  }, []);

  useEffect(() => {
    selecionarBenchmarks(
      loadedCarteiraList,
      carteiraList,
      carteira,
      dispatchPDF,
      selectedBenchmarks,
      'PerfHist',
      dispatchSelectedBenchmarks,
    );
  }, [loadedCarteiraList]);

  useEffect(() => {
    if (isLoaded && !dispatchPDF) dispatch(sendAnalyticsData(location.href));
  }, [params, isLoaded]);

  useFetchAPIs(isLoaded, loading, dispatchPDF);

  /**
   * Carregar os benchmarks quando a página for recarregada
   */
  useEffect(() => {
    const bench_history = uselocation?.state?.bench_perf_hist ?? null;
    if (bench_history) {
      dispatch(setSelectedBenchmarksPerfHist(bench_history));
    }
  }, []);

  const assetsApi = isLoaded && graficoRetornos ? (graficoRetornos[0].slice(1) as string[]) : null;
  const dados = isLoaded && graficoRetornos ? graficoRetornos.slice(1) : null;
  const dadosEvol = isLoaded && graficoPL ? graficoPL.slice(1) : null;

  const datesApi = isLoaded ? formatDatesApi(graficoRetornos) : null;

  const dates = formatDates(datesApi);

  const datesTooltip = datesApi?.map((item: any) => item.split('-').reverse().join('/'));
  const data = formatData(assetsApi, dados, dates, datesTooltip);
  const dataEvol = formatDataEvol(assetsApi, dadosEvol, dates, datesTooltip);

  const moneyPrefix = MoneyPrefix(carteiraList, params.carteira);

  const [openModal, setOpenModal] = useState(false);
  const [periodoModal, setPeriodoModal] = useState('');
  const [isPercentToggle, setIsPercentToggle] = useState(false);
  const [tabelaSubPercent, setTabelaSubPercent] = useState(false);
  const [classPeriodData, setClassPeriodData] = useState<{
    class: string;
    value: number | null;
    data: IRendimentoSubclasse[];
  }>({ class: '', value: null, data: [] });
  const [viewClassOnPeriod, setViewClassOnPeriod] = useState(false);

  const onBarClickHandle = (value: any) => {
    setOpenModal(!openModal);
    if (value?.payload?.name) {
      setViewClassOnPeriod(false);
      setPeriodoModal(value?.payload?.name);
    } else {
      setPeriodoModal('');
    }
  };
  const onSecondBarHandle = (value: any) => {
    setViewClassOnPeriod(true);
    setClassPeriodData({
      class: value?.payload.name,
      value: value?.payload.value[1]
        ? value?.payload.value[1] - value?.payload.value[0]
        : value?.payload.value,
      data: value?.payload?.auxTableData ?? [],
    });
  };

  const dadosPercentPerfPages = dadosPercentPerf?.[`perf_${periodoModal}`] ?? [];

  interface GroupedDataRetornos {
    [year: number | string]: (number | string)[][];
  }

  const invertDadosMesaMesAgrupados = (dados: GroupedDataRetornos) => {
    const entries = Object.entries(dados);
    return preferenceCustomizacao?.PerfHist_inverterMesaMes ? entries.reverse() : entries;
  };

  const dadosMesaMesAgrupados = formatDadosMesaMesAgrupados(dadosMesaMes);
  const columnsRetornos = getColumnsRetornos(dadosMesaMesAgrupados, theme);
  const rowsRetornos = isLoaded
    ? formatRowsRetornos(dadosMesaMesAgrupados, invertDadosMesaMesAgrupados)
    : null;

  const columnsMovim = getColumnsAplRes();
  const rowsMovim = isLoaded ? formatRowsAplRes(movimentacoes) : null;

  const colsTabelaRendClasseMes = getColsTabelaRendClasseMes(isPercentToggle, periodoModal);
  const rowsTabelaRendClasseMes = isLoaded
    ? formatRowsTabelaRendClasseMes(classPeriodData, moneyPrefix, theme)
    : null;
  const rowsTabelaRendClasseMesPercent = isLoaded
    ? formatRowsTabelaRendClasseMesPercent(classPeriodData, dadosPercentPerfPages, theme)
    : null;

  const barChartModal = isLoaded ? formatBarChartModal(rendimento12meses, periodoModal) : null;
  const barChartRend12Data = isLoaded ? formatBarChartRend12Data(rendimento12meses) : null;
  const barChartModalPercent = formatBarChartModalPercent(dadosPercentPerfPages);
  const waterfallChartModal = isLoaded
    ? formatWaterfallChartModal(
        preferenceCustomizacao,
        rendimento12meses,
        periodoModal,
        dadosPercentPerfPages,
        isPercentToggle,
      )
    : null;
  let titulosTabelaAplicacoesResgate = null;
  if (preferenceCustomizacao?.PerfHist_arrayTabelaAplicacoesResgate) {
    titulosTabelaAplicacoesResgate = JSON.parse(preferenceCustomizacao?.PerfHist_arrayTabelaAplicacoesResgate);
  }

  if (preferenceCustomizacao?.PerfHist_shiftTabAplicacoes ?? false) {
    rowsMovim?.shift();
  }

  useEffect(() => {
    setIsPercentToggle(false);
    setTabelaSubPercent(false);
  }, [openModal]);

  const handleToggleModal = async () => {
    await dispatch(fetchPerfHistPerf(periodoModal));
    setIsPercentToggle(!isPercentToggle);
    setTabelaSubPercent(!tabelaSubPercent);
  };

  useEffect(() => {
    if (dispatchPDF) {
      const data_perfHist = {
        rowsRetornos,
        dadosMesaMesAgrupados,
        columnsRetornos,
        rowsMovim,
        columnsMovim,
        data,
        dataEvol,
        assetsApi,
        barChartRend12Data,
        moneyPrefix,
        obj_datas,
      };
      sendData('PerfHist', data_perfHist);
    }
  }, []);

  if (dispatchPDF) {
    return <></>;
  }

  const toggleColorAbsolute = isPercentToggle ? 'gray' : theme.toggleSwitchColor;
  const toggleColorPercent = isPercentToggle ? theme.toggleSwitchColor : 'gray';

  const StyledSwitch = styled(Switch)(() => ({
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: theme.toggleSwitchColor,
    },
  }));

  const componentGrafRetornoAcumulado = (
    <Grid
      item
      xs={12}
      md={preferenceCustomizacao?.PerfHist_retornoAcumuladoTopo ?? false ? 12 : 6}
      sx={preferenceCustomizacao?.PerfHist_retornoAcumuladoTopo ? { pt: '0px !important' } : {}}>
      <GraficoRetornoAcumulado
        data={data}
        assets={assetsApi}
        moneyPrefix={moneyPrefix}
        isFullWidth={preferenceCustomizacao?.PerfHist_removerGrafEvPatrimonio}
      />
    </Grid>
  );

  const componentGrafEvolucaoPatrimonio = (
    <Grid item xs={12} md={preferenceCustomizacao?.PerfHist_retornoAcumuladoTopo ?? false ? 12 : 6}>
      <GraficoEvolucaoPatrimonio data={dataEvol} moneyPrefix={moneyPrefix} />
    </Grid>
  );

  const elementTabelas =
    preferenceCustomizacao?.PerfHist_isBarChart ?? true ? (
      <GraficoContribuicaoFinanceira
        titulo=''
        data={isPercentToggle ? barChartModalPercent : barChartModal}
        GraphComponent={GraficoBarra}
        theme={theme}
        isPercentToggle={isPercentToggle}
        onClickHandle={onSecondBarHandle}
        moneyPrefix={moneyPrefix}
      />
    ) : (
      <GraficoContribuicaoFinanceira
        titulo=''
        data={waterfallChartModal}
        GraphComponent={GraficoWaterfall}
        theme={theme}
        isPercentToggle={isPercentToggle}
        onClickHandle={onSecondBarHandle}
        moneyPrefix={moneyPrefix}
        isHorizontal={preferenceCustomizacao?.PerfHist_isHorizontal}
        layout='waterfall'
      />
    );

  return (
    <>
      <Grid>
        <HeaderWrapper
          title="Performance Histórica"
          containerAux
          benchmarks={{ selectedBenchmarks, dispatchSelectedBenchmarks, customItemOptions }}
          periodo
          obj_datas={obj_datas}
          subtitle_date
        />
        <Line />
      </Grid>
      <Grid container spacing={3} sx={{ marginTop: 3 }}>
        {preferenceCustomizacao?.PerfHist_retornoAcumuladoTopo ?? false
          ? componentGrafRetornoAcumulado
          : null}
        <Grid item xs={12}>
          <TabelaRetornosNominais
            rows={rowsRetornos}
            dadosMesAMesAgrupados={dadosMesaMesAgrupados}
            theme={theme}
          />
        </Grid>
        <Grid item xs={12}>
          <TabelaAplicacoesResgate rows={rowsMovim} titles={titulosTabelaAplicacoesResgate}/>
        </Grid>
        {!(preferenceCustomizacao?.PerfHist_retornoAcumuladoTopo ?? false)
          ? componentGrafRetornoAcumulado
          : null}
        {preferenceCustomizacao?.PerfHist_removerGrafEvPatrimonio ?? false
          ? null
          : componentGrafEvolucaoPatrimonio}
        <Grid item xs={12}>
          <GraficoGanhoFinanceiro
            data={barChartRend12Data}
            theme={theme}
            moneyPrefix={moneyPrefix}
            onClickHandle={onBarClickHandle}
          />
        </Grid>
      </Grid>
      <CustomModal
        open={openModal}
        onCloseHandle={onBarClickHandle}
        width={viewClassOnPeriod ? 'fit-content' : '80%'}>
        {viewClassOnPeriod ? (
          <div>
            <div
              onClick={() => setViewClassOnPeriod(false)}
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                width: 'fit-content',
              }}>
              <FontAwesomeIcon
                icon={faArrowCircleLeft as IconProp}
                style={{ color: theme.backgroundColorMain, margin: '8 8 8 0' }}
                size="lg"
              />
              Voltar
            </div>
            <EnhancedTable
              rows={isPercentToggle ? rowsTabelaRendClasseMesPercent : rowsTabelaRendClasseMes}
              columns={colsTabelaRendClasseMes}
              height={300}
            />
          </div>
        ) : (
          <>
            <Grid container>
              <Grid item>
                <Subtitulo mb="8px">
                  {isPercentToggle ? 'Atribuição de Performance' : 'Contribuição Financeira'} no Mês
                  ({periodoModal})
                  {rendimento12meses[periodoModal]
                    ? ` - Total da carteira: ${
                        isPercentToggle
                          ? helperFormat(
                              dadosPercentPerfPages && dadosPercentPerfPages[0]
                                ? dadosPercentPerfPages[0].perf
                                : '',
                              FormatTypes.percentage,
                              2,
                              moneyPrefix,
                            )
                          : helperFormat(
                              rendimento12meses[periodoModal]?.[0]?.rend,
                              FormatTypes.monetary,
                              2,
                              moneyPrefix,
                            )
                      }`
                    : ''}{' '}
                </Subtitulo>
              </Grid>
              <Grid item style={{ marginLeft: 'auto' }}>
                <IconButton style={{ color: toggleColorAbsolute }} onClick={handleToggleModal}>
                  <FontAwesomeIcon
                    icon={faDollarSign as IconProp}
                    style={{ color: toggleColorAbsolute }}
                    size="xs"
                  />
                </IconButton>
                <StyledSwitch
                  checked={isPercentToggle}
                  onChange={handleToggleModal}
                  style={{ color: theme.toggleSwitchColor }}
                />
                <IconButton style={{ color: toggleColorPercent }} onClick={handleToggleModal}>
                  <Percent />
                </IconButton>
              </Grid>
            </Grid>
            <Grid style={{ marginTop: isMobile ? -50 : '' }}>{elementTabelas}</Grid>
          </>
        )}
      </CustomModal>
      <LoadingModal loading={(loadingPercent && !isLoadingMinimized) ?? false} showMinimizar={false} />
    </>
  );
}
